import api from '@/shared/api';
import { formatDateForApi } from '@/shared/lib';
import {
  withQuery,
  type ApiResponse,
  type BulkDeleteResult,
  requestExcelBlobExport,
  type ExcelBlobExportResult,
} from '@/shared/lib';
import type { AxiosResponse } from 'axios';
import type {
  PaginatedUserResponse,
  PaginationMetadata,
  User,
  UserSearchParams,
} from '../model/types';

interface UserResponse {
  success: boolean;
  data: User;
}

interface CreateUserResponse {
  user: User;
  generatedPassword: string;
}

interface UserPosition {
  id: number;
  name: string;
  departmentId?: number;
  active?: boolean;
}

class UserApi {
  async getAllUsers(listParams?: { itemsPerPage?: number; page?: number }): Promise<ApiResponse<User[]>> {
    const params: Record<string, number> = {};
    if (listParams?.itemsPerPage != null) {
      params.itemsPerPage = listParams.itemsPerPage;
    }
    if (listParams?.page != null) {
      params.page = listParams.page;
    }

    const response =
      Object.keys(params).length > 0
        ? await api.get<ApiResponse<User[]>>('/users', { params })
        : await api.get<ApiResponse<User[]>>('/users');
    return response.data;
  }

  async getUsers(): Promise<User[]> {
    const response = await api.get<ApiResponse<User[]>>('/users');
    return response.data.data || [];
  }

  async getPositionsByUserId(userId: number | string): Promise<UserPosition[]> {
    const response = await api.get<ApiResponse<UserPosition[]>>(`/positions/user/${userId}`);
    return response.data.data || [];
  }

  async getCurrentUser(): Promise<AxiosResponse<UserResponse>> {
    const response = await api.get<UserResponse>('/auth/me');
    return response;
  }

  async getUserById(userId: number): Promise<User> {
    const response = await api.get<ApiResponse<User>>(`/users/${userId}`);
    if (!response.data.data) {
      throw new Error('User not found');
    }
    return response.data.data;
  }

  async getUsersByPosition(positionId: number): Promise<User[]> {
    const response = await api.get<ApiResponse<User[]>>(`/users/position/${positionId}`);
    return response.data.data || [];
  }

  async search(params: UserSearchParams): Promise<{ data: User[]; metadata: PaginationMetadata }> {
    const response: AxiosResponse<PaginatedUserResponse> = await api.get(
      withQuery('/users', params)
    );

    return {
      data: response.data.data ?? [],
      metadata: response.data.metadata ?? {
        total: 0,
        page: 1,
        itemsPerPage: 10,
        totalPages: 0,
      },
    };
  }

  async exportUsersExcel(params: UserSearchParams): Promise<ExcelBlobExportResult> {
    return requestExcelBlobExport(
      () =>
        api.get(withQuery('/users/export/excel', params), {
          responseType: 'blob',
        }),
      'usuarios.xlsx',
      'Tente novamente.'
    );
  }

  async getUsersByPositions(positionIds: number[]): Promise<User[]> {
    if (positionIds.length === 0) {
      return [];
    }
    const response = await api.post<ApiResponse<User[]>>('/users/by-positions', { positionIds });
    return response.data.data || [];
  }

  async createUserWithMember(data: CreateUserWithMemberData): Promise<CreateUserResponse> {
    const positionIds = data.positions.map((p) => p.positionId).filter(Boolean);

    const response = await api.post<ApiResponse<CreateUserResponse>>('/users', {
      name: data.name,
      username: data.username,
      email: data.email,
      positionIds: positionIds.length ? positionIds : undefined,
      registrationNumber: data.registrationNumber,
      admissionDate: data.admissionDate ? (formatDateForApi(data.admissionDate) ?? undefined) : undefined,
      dismissalDate: data.dismissalDate ? (formatDateForApi(data.dismissalDate) ?? undefined) : undefined,
      status: data.status,
      role: data.role,
      functionalActivityIds: data.functionalActivityIds?.length
        ? data.functionalActivityIds
        : undefined,
    });

    if (!response.data.data) {
      throw new Error('Falha ao criar usuário');
    }

    return response.data.data;
  }

  async resetPassword(userId: number): Promise<{ generatedPassword: string }> {
    const response = await api.post<ApiResponse<{ generatedPassword: string }>>(
      `/users/${userId}/reset-password`
    );
    if (!response.data.data) {
      throw new Error('Falha ao resetar senha');
    }
    return response.data.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await api.delete(`/users/${userId}`);
  }

  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/users/${id}`, data);
    if (!response.data.data) {
      throw new Error('Falha ao atualizar usuário');
    }
    return response.data.data;
  }

  async bulkDelete(userIds: number[]): Promise<{ data: BulkDeleteResult }> {
    const response = await api.delete('/users/bulk', { data: { ids: userIds } });
    return { data: response.data.data };
  }
}

export interface UpdateUserData {
  name?: string;
  username?: string;
  email?: string;
  registrationNumber?: string;
  admissionDate?: string | null;
  dismissalDate?: string | null;
  status?: string;
  role?: 'USER' | 'SUPERUSER';
}

export interface CreateUserWithMemberData {
  name: string;
  username: string;
  email: string;
  positions: { departmentId: number; positionId: number }[];
  registrationNumber?: string;
  admissionDate?: Date | null;
  dismissalDate?: Date | null;
  status?: string;
  role?: 'USER' | 'SUPERUSER';
  functionalActivityIds?: number[];
}

export const userApi = new UserApi();
